<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
import CountdownTimer from './CountdownTimer.vue';

// // Store management imports
import { useAwsStore } from '@/stores/aws';
import { useBreakStore } from '@/stores/break';
import { useGeneralStore } from '@/stores/general';
import { useTimeStore } from '@/stores/time';
import { useTrackerStore } from '@/stores/tracker';

// // Utils imports
import dataAnalyzers, { BreakModeEnum } from '@/utils/dataAnalyzers';
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { logDebug, logError, logWarn } from '@/utils/logger';

// // Composables
import { usePollResults } from '@/composables/usePollResults';

// // Module imports
import PollInput from './PollInput.vue';
import PollOutput from './PollOutput.vue';
import { PollSimpleOptions } from './pollSimpleUtils';

// // Asset imports
import pollUseRemoteSrc from '@/assets/images/simple-polls/instructions-s-poll-use-remote.png';
import pollRemoteTopFadeSrc from '@/assets/images/icons/instruction-s-remote-top-fade-down.png';
import pollJoBrosHeroLogoWhiteRedSrc from '@/assets/images/simple-polls/hero-jb-live-white-red.png';
import pollJoBrosHeroLogoGoldWhiteSrc from '@/assets/images/simple-polls/hero-jb-live-gold-white.png';
import pollTvPOriginalsHeroSrc from '@/assets/images/simple-polls/hero-s-tvp-originals-white-transparent.png';
import pollTvPExclusivesHeroSrc from '@/assets/images/simple-polls/hero-s-tvp-exclusives-white-transparent.png';
import pollGHHeroSrc from '@/assets/images/simple-polls/hero-s-gh-white-transparent.png';
import joBrosBackground from '@/assets/images/simple-polls/JonasBackground-Square-MO-3X.jpg';
import { toggleVideoSound } from '@/utils/videoUtils';

const storeAws = useAwsStore();
const storeBreak = useBreakStore();
const storeGeneral = useGeneralStore();
const storeTime = useTimeStore();
const storeTracker = useTrackerStore();

const isKeyCurrentlyPressed = ref(false);
const hasOptionBeenSelected = ref(false);
const hasNavigationFrozen = ref(false);
const isMobile = ref(false);
const isReadyToShow = ref(false);
const hasSelectedOptionLclStrge = ref(false);
const hasSentMobileQrScanLclStrge = ref(false);
const activeIndex = ref(0);
const pressedKey = ref('');
const scanMobileTag = ref('');
const scanCampaignTag = ref('');
const optionSelectedLclStrge = ref(-1);
const resetLSCounter = ref(0);
const countdownSeconds = ref(27);

const appInstance = import.meta.env.VITE_APP_ENV;
const trackPrefix = `${import.meta.env.VITE_GLOBAL_COMMUNICATION_URL}/track/`;
const catchErrorPrefix = import.meta.env.VITE_APP_CATCH_ERROR_PREFIX;

let intervalId = null;
// // To store the AWS credential provider (reactive)
const awsCredentialProvider = ref(null);

const { calculatedPollPercentages, getSelectionHandler, castVote } = usePollResults({
  optionKeys: Object.values(PollSimpleOptions),
  getBreakUuid: () => storeBreak.breakUuid,
  getAwsCredentialProvider: () => awsCredentialProvider.value,
});

// Video element references for full mode only
const isMuted = ref(true); // Start muted by default to comply with autoplay policies
const videoRef = ref(null);

const toggleSound = () => {
  toggleVideoSound(videoRef, isMuted);
};

// // PIPPoll handler functions

const keyDownHandler = (e) => {
  // console.log(`You pressed ${e.key}: ${e.code}.`);

  CommunicationUtil.onKeyPressed(e.keyCode);
  isKeyCurrentlyPressed.value = true;
  pressedKey.value = e.key;
};

const keyUpHandler = () => {
  // console.log(`You are no longer pressing ${e.key}: ${e.code}.`);

  isKeyCurrentlyPressed.value = false;
  pressedKey.value = '';
};

const deviceTypeDetectionHelper = (deviceTypeDataParam) => {
  const mobileMatch = deviceTypeDataParam.match(
    /Android|BlackBerry|IEMobile|iPad|iPhone|iPod|webOS|Windows Phone/i,
  );
  let results = false;

  if (mobileMatch) {
    results = true;
  } else {
    results = false;
  }

  return results;
};

const mobileDetectionHandler = () => {
  if (navigator.userAgentData) {
    return navigator.userAgentData
      .getHighEntropyValues([
        // 'architecture',
        // 'bitness',
        // 'brands',
        'mobile',
        // 'model',
        // 'platform',
        // 'platformVersion',
        // 'fullVersionList',
      ])
      .then((userAgentValue) => {
        // isMobile.value = true;
        isMobile.value = userAgentValue.mobile;

        // return true;
        return userAgentValue;
      });
  } else {
    // isMobile.value = true;
    isMobile.value = deviceTypeDetectionHelper(navigator.userAgent);
  }
};

const options = computed(() => {
  if (
    [
      'eed59c7d-c452-4f98-971b-44ff52580fe0',
      '963dd394-a62c-47bc-8378-46075544222d',
      '2afef29d-d9a0-42b9-a9fe-29ccdc577314',
    ].includes(storeBreak.breakUuid)
  ) {
    // Dec 2025 Jonas Brothers template
    return {
      tvStyle: {
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.53) 0%, #4D412D 34.62%, #2c2c2c 78.37%, rgba(66, 52, 42, 0.12) 100%)',
      },
      mobileStyle: {
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.53) -15.14%, #4D412D 29.83%, #2c2c2c 86.67%, rgba(66, 52, 42, 0.12) 114.77%)',
      },
      inputHeading: 'Fans Decide',
      pollOutputColors: {
        bar: 'linear-gradient(90deg, #956831 0%, #D8B77E 137.72%)',
        resultsBox: 'rgba(0, 0, 0, 0.3)',
      },
      activeButtonBorderColor: '#C9AD78',
      pollMobileBackground: `url(${joBrosBackground})`,
      countdownTimerActiveColor: '#ffca45',
      countdownTimerInactiveColor: '#655f54',
      hero: {
        logo: pollJoBrosHeroLogoGoldWhiteSrc,
        tvp: pollTvPExclusivesHeroSrc,
      },
      footer: pollGHHeroSrc,
    };
  } else if (
    [
      '4f41cd54-7d0b-4180-b389-b86c430d9a71',
      '26d7e416-ed5e-4abc-bdba-81ff3e2121b6',
      '8ac58c4b-f1eb-43d0-af6c-294228ac518e',
      '1949b3a7-728f-4a1b-a3ca-5a21774086dd',
    ].includes(storeBreak.breakUuid)
  ) {
    // Oct 2025 Jonas Brothers template
    return {
      tvStyle: {
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.53) 0%, #412E20 20.62%, #412E20 78.37%, rgba(66, 52, 42, 0.12) 100%)',
      },
      mobileStyle: {
        background: 'linear-gradient(180deg, #412E20 0%, rgba(65, 46, 32, 0.73) 100%)',
      },
      inputHeading: 'Fans Decide',
      pollOutputColors: {
        bar: 'rgba(178, 13, 25, 1)',
        resultsBox: 'rgba(30, 30, 40, 1)',
      },
      activeButtonBorderColor: 'rgba(178, 13, 25, 1)',
      pollMobileBackground:
        'linear-gradient(180deg, #000 -12%, #b20d19 70.19%, rgba(0, 0, 0, 1) 80%)',
      countdownTimerActiveColor: '#b20d19',
      countdownTimerInactiveColor: '#655f54',
      hero: {
        logo: pollJoBrosHeroLogoWhiteRedSrc,
        tvp: pollTvPOriginalsHeroSrc,
      },
      footer: null,
    };
  } else {
    // All other templates
    return {
      tvStyle: {
        background:
          'linear-gradient(180deg, #1E143C 0%, #1E143C 35.5%, #1E143C 73.4%, rgba(30, 20, 60, 0.52) 100%)',
      },
      mobileStyle: {
        background: 'linear-gradient(180deg, #1E143C 0%, rgba(30, 20, 60, 0.73) 100%)',
      },
      inputHeading: 'Cast Your Vote',
      pollOutputColors: {
        bar: 'rgba(124, 57, 151, 1)',
        resultsBox: 'rgba(52, 45, 73, 1)',
      },
      activeButtonBorderColor: 'rgba(137, 0, 255, 1)',
      pollMobileBackground: 'black',
      countdownTimerActiveColor: '#d5bff4',
      countdownTimerInactiveColor: '#582c68',
      hero: null,
      footer: pollGHHeroSrc,
    };
  }
});

const backgroundHandler = () => {
  if (isMobile.value) {
    return options.value.mobileStyle;
  } else {
    return options.value.tvStyle;
  }
};

const updateLocalStoragePoll = () => {
  hasSelectedOptionLclStrge.value = true;
  localStorage.setItem(
    `${storeBreak.breakUuid}-lSHasSelectedOption`,
    hasSelectedOptionLclStrge.value,
  );
  optionSelectedLclStrge.value = activeIndex.value;
  localStorage.setItem(`${storeBreak.breakUuid}-lSOptionSelected`, optionSelectedLclStrge.value);
};

const updateLocalStorageQrScanTracker = () => {
  hasSentMobileQrScanLclStrge.value = true;
  localStorage.setItem(
    `${storeBreak.breakUuid}-lSHasSentMobileQrScan`,
    hasSentMobileQrScanLclStrge.value,
  );
};

const buttonSelectHandler = async () => {
  // castVote handles the get-then-put fallback, the optimistic result, and
  // scheduling the graceful update + bounded retry — see usePollResults.
  await castVote(activeIndex.value, storeBreak.breakOptions[activeIndex.value].optionLabel);

  hasOptionBeenSelected.value = true;

  // // Update beacon with accept invitation event
  if (storeTracker.isIframe && ['dev', 'stage', 'prod'].includes(appInstance)) {
    if (storeTracker.trackingInfo) {
      if (
        storeTracker.trackingInfo.trackingEvents &&
        storeTracker.trackingInfo.trackingEvents.length > 0
      ) {
        storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
          storeTracker.trackingInfo,
          'acceptInvitation',
          storeBreak.breakUuid,
        );
      }
    }
  }

  updateLocalStoragePoll();
};

// // PIPPoll functions

const getMobileFlag = computed(() => {
  // return true;
  return isMobile.value;
});

const shouldShowResults = computed(() => {
  // This computed property will automatically update when hasOptionBeenSelected changes.
  return hasOptionBeenSelected.value;
});

const selectedPollOption = computed(() => {
  // This computed property reactively returns the selected poll option.
  return Number(optionSelectedLclStrge.value);
});

const getTimeLeft = computed(() => {
  // // If we have valid time data, calculate accurate time remaining
  if (storeTime.hasValidTimeData) {
    const timeDelta = storeTime.timeDeltaFromStart;
    const fadeOutEnd = pipPollTimerConfig.value.fadeOutEnd;
    const timeRemaining = Math.max(0, Math.ceil(fadeOutEnd - timeDelta));
    return timeRemaining;
  }

  // // Fallback to the original countdown if no time data available
  return countdownSeconds.value;
});

// // Time-based computed properties for conditional timer visibility
// // Using the optimized time store to minimize re-renders
const shouldShowTimerBasedOnTime = computed(() => {
  return storeTime.timerVisibilityState.shouldShow;
});

const timerAnimationState = computed(() => {
  return storeTime.timerVisibilityState.animationState;
});

// // Configure timer settings for this component (dynamic based on video duration)
const pipPollTimerConfig = computed(() => {
  // // Get video duration from break store, fallback to 30 seconds if not available
  const videoDuration = parseFloat(storeBreak.breakPlaybackVideoDuration) || 30;

  return {
    fadeInStart: 1, // // seconds - when to start fading in
    fadeInEnd: 3.5, // // seconds - when fade in completes
    fadeOutStart: videoDuration - 6, // // seconds - when to start fading out
    fadeOutEnd: videoDuration - 5, // // seconds - when fade out completes
  };
});

// // Configure app container settings for this component (different timing than timer)
const appContainerTimerConfig = computed(() => {
  // // Get video duration from break store, fallback to 30 seconds if not available
  const videoDuration = parseFloat(storeBreak.breakPlaybackVideoDuration) || 30;

  return {
    fadeInStart: 3, // // seconds - when to start fading in the app container
    fadeInEnd: 5, // // seconds - when fade in completes
    fadeOutStart: videoDuration - 3, // // seconds - when to start fading out
    fadeOutEnd: videoDuration - 1, // // seconds - when fade out completes
  };
});

// // Time-based computed properties for app container visibility
const shouldShowAppContainer = computed(() => {
  if (isMobile.value) {
    return true;
  } else {
    // // Get visibility state using app container config
    const visibility = storeTime.getTimerVisibility(appContainerTimerConfig.value);
    return visibility.shouldShow;
  }
});

const handlePokeData = (dataParam) => {
  activeIndex.value = dataParam;
  buttonSelectHandler();
};

// // PIPPoll lifecycle hooks

onBeforeMount(async () => {
  try {
    // // 1. Check for existing selection in localStorage
    const storedLocalSelectionFlag = localStorage.getItem(
      `${storeBreak.breakUuid}-lSHasSelectedOption`,
    );
    const storedLocalSendMobileQrScanFlag = localStorage.getItem(
      `${storeBreak.breakUuid}-lSHasSentMobileQrScan`,
    );
    const storedLocalSelectionOption = localStorage.getItem(
      `${storeBreak.breakUuid}-lSOptionSelected`,
    );
    hasSelectedOptionLclStrge.value = storedLocalSelectionFlag === 'true';
    hasSentMobileQrScanLclStrge.value = storedLocalSendMobileQrScanFlag === 'true';
    optionSelectedLclStrge.value = storedLocalSelectionOption
      ? Number(storedLocalSelectionOption)
      : -1;

    // // 0. Initialize AWS Cognito credentials and backendApis first
    const identityPoolId = import.meta.env.VITE_APP_IDENTITY_POOL_ID;

    if (identityPoolId) {
      // Initialize AWS credentials and signer
      if (storeAws.isInitialized.value && storeAws.awsCredentialProvider.value) {
        awsCredentialProvider.value = storeAws.awsCredentialProvider.value;
      } else {
        awsCredentialProvider.value = await storeAws.initializeAwsCredentials(
          identityPoolId,
          import.meta.env.VITE_APP_AWS_REGION || 'us-east-1',
        );
      }
      // Note: API calls will be made in the isBreakReady watcher to avoid race condition
    } else {
      logWarn('VITE_APP_IDENTITY_POOL_ID is not set. AWS calls may not be authenticated.');
    }
  } catch (error) {
    logError('Error during onBeforeMount in TempPIPPoll:', error);
  }
});

// // IMPORTANT: This onMounted hook is now async.
// // The <Suspense> component in the parent will wait for this to complete.
onMounted(async () => {
  // // 1. Detect device type first
  await mobileDetectionHandler();

  // // 2. Keep the original delay for consistency.
  // await new Promise((resolve) => setTimeout(resolve, 850));
  console.log('Initial delay complete (commented out).');
  logDebug('Initial delay complete (commented out).');

  // // 3. Set up event listeners and start the timer.
  // //    This happens after fonts and delay.
  document.addEventListener('keydown', keyDownHandler);
  document.addEventListener('keyup', keyUpHandler);

  isReadyToShow.value = true;
  // countdownTimer();
  fetch(
    `${catchErrorPrefix}${appInstance}/appReachable?is_tizen=${storeTracker.isTizen}&called_from=pip`,
  );
  // // Initialize time manager for parent window communication
  // // Time store will automatically detect if spoof timer should be used
  try {
    // // Now that we know the Tizen status, start the appropriate timer
    if (
      !storeTracker.isIframe ||
      [BreakModeEnum.FULLMODE, BreakModeEnum.BROWSER].includes(storeBreak.breakDisplayMode)
    ) {
      logDebug('Using spoof timer (Tizen is false OR mode set to full/browser)');
      storeTime.startSpoofTimer(500); // // Update every 500ms to simulate real behavior
    } else {
      // // Use real parent window communication for production
      logDebug('Using real time manager (Tizen is true)');
      storeTime.initializeTimeManager();
    }

    // // Apply custom timer configuration for this component
    storeTime.updateTimerConfig(pipPollTimerConfig.value);
  } catch (error) {
    logError('Error initializing time manager:', error);
    // // Continue without time manager if it fails
  }

  if (isMobile.value) {
    logDebug('Setting up mobile refresh interval');
    intervalId = setInterval(() => {
      // logDebug('Refresh results for mobile');
      logDebug('Mobile refresh interval triggered');
      getSelectionHandler('mobileRefresh');
    }, 30000);

    await dataAnalyzers
      .getQueryStringScanTypeData(storeBreak.breakSearchParameters)
      .then(async (capturedScanData) => {
        scanMobileTag.value = capturedScanData.mobileData;
        scanCampaignTag.value = capturedScanData.campaignData;

        if (!hasSentMobileQrScanLclStrge.value) {
          fetch(
            `${trackPrefix}${appInstance}/qrScan?ibUuid=${storeBreak.breakUuid}&mobile=${capturedScanData.mobileData}&campaign=${capturedScanData.campaignData}`,
          );

          updateLocalStorageQrScanTracker();
        }
      });
  }
});

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }

  // // Clean up time manager
  storeTime.stopSpoofTimer();
  storeTime.deactivateTimeManager();
});

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
  document.removeEventListener('keyup', keyUpHandler);
});

// // PIPPoll watchers

watch(isKeyCurrentlyPressed, (isKeyCurrentlyPressedNewVal) => {
  if (isKeyCurrentlyPressedNewVal && !isMobile.value) {
    // console.log('PIPPoll – Key is pressed down');
    // console.log(`pressedKey.value: ${pressedKey.value}`);

    if (!hasOptionBeenSelected.value && !hasNavigationFrozen.value) {
      if (pressedKey.value === 'ArrowDown') {
        if (activeIndex.value < storeBreak.breakOptions.length - 1) {
          activeIndex.value++;
        }
      } else if (pressedKey.value === 'ArrowUp') {
        if (activeIndex.value > 0) {
          activeIndex.value--;
        }
      } else if (pressedKey.value === 'Enter') {
        hasNavigationFrozen.value = true;

        // logDebug(`Button Selected – optionLabel: ${props.pollOptions[activeIndex.value].optionLabel}`);
        logDebug(
          `Button Selected – optionLabel: ${storeBreak.breakOptions[activeIndex.value].optionLabel}`,
        );

        buttonSelectHandler();
      }
    } else {
      if (pressedKey.value === 'ArrowLeft' && storeGeneral.instance.designation !== 'prod') {
        resetLSCounter.value++;
      }
    }
  }
});

watch(resetLSCounter, (resetLSCounterNewVal) => {
  if (resetLSCounterNewVal && resetLSCounterNewVal === 7) {
    logDebug('Clearing poll localStorage data');
    // Only clear poll-specific data
    localStorage.removeItem(`${storeBreak.breakUuid.value}-lSHasSelectedOption`);
    localStorage.removeItem(`${storeBreak.breakUuid.value}-lSOptionSelected`);
    // Reset the counter so it can be used again
    resetLSCounter.value = 0;
  }
});

// Watch for isBreakReady to become true before making API calls
// This prevents the race condition where breakUuid is not yet available
watch(
  [() => storeBreak.isBreakReady, awsCredentialProvider],
  async ([isBreakReadyNewVal, credentialProvider]) => {
    if (isBreakReadyNewVal && credentialProvider) {
      logDebug('isBreakReady is true and credentials are ready, fetching poll data');
      // Determine if previous vote present, if so, call results and move on else call in background
      if (hasSelectedOptionLclStrge.value) {
        // User has already voted, fetch results and show PollOutput
        await getSelectionHandler('isBreakReadyWatcher').then(
          () => (hasOptionBeenSelected.value = true),
        );
      } else {
        // No vote yet, fetch initial results in background
        getSelectionHandler('isBreakReadyWatcher');
      }
    }
  },
);
</script>

<template>
  <section
    class="pip-poll"
    :class="{ 'is-mobile': getMobileFlag }"
    :style="{
      '--active-button-border-color': options.activeButtonBorderColor,
      '--poll-mobile-background': options.pollMobileBackground,
    }"
  >
    <!-- Background video - Only show in full mode -->
    <video
      v-if="
        storeBreak.breakDisplayMode === 'full' && storeBreak.breakPlaybackVideoSrc && !getMobileFlag
      "
      ref="videoRef"
      class="pip-poll__background-video"
      :src="storeBreak.breakPlaybackVideoSrc"
      autoplay
      loop
      :muted="isMuted"
      playsinline
    ></video>

    <div
      v-if="
        storeBreak.breakDisplayMode === 'full' && storeBreak.breakPlaybackVideoSrc && !getMobileFlag
      "
      class="pip-poll__video-click-overlay"
      @click="toggleSound"
    ></div>
    <!-- End background video - Only show in full mode -->

    <div
      class="pip-poll__container"
      :class="{
        'is-hidden': !isReadyToShow,
        'is-clear': !shouldShowAppContainer,
        'is-opaque': shouldShowAppContainer,
      }"
    >
      <div v-if="getMobileFlag && options.hero" class="pip-poll__hero">
        <img class="pip-poll__hero--jb" :src="options.hero.logo" />

        <img class="pip-poll__hero--tvp" :src="options.hero.tvp" />
      </div>

      <div v-if="getMobileFlag && options.footer" class="pip-poll__footer">
        <img class="pip-poll__footer--general" :src="options.footer" />
      </div>

      <div
        class="pip-poll__timer"
        :class="{
          'is-gone': getMobileFlag || !shouldShowTimerBasedOnTime,
          'is-clear': timerAnimationState === 'hidden',
          'is-opaque': timerAnimationState === 'visible',
          'pip-poll__timer--fading-in': timerAnimationState === 'fading-in',
          'pip-poll__timer--fading-out': timerAnimationState === 'fading-out',
        }"
      >
        <div class="pip-poll__countdown">{{ getTimeLeft }}</div>

        <CountdownTimer
          v-if="!getMobileFlag && isReadyToShow && shouldShowTimerBasedOnTime"
          class="pip-poll__tally-ring"
          :duration="pipPollTimerConfig.fadeOutEnd - 1"
          :active-color="options.countdownTimerActiveColor"
          :inactive-color="options.countdownTimerInactiveColor"
        />
      </div>

      <PollInput
        v-show="!shouldShowResults"
        :is-interaction-mobile="getMobileFlag"
        :input-box-background="backgroundHandler()"
        :ad-vote-state="options.inputHeading"
        :ad-headline="storeBreak.breakHeadlineCopy"
        :ad-headline-color="storeBreak.breakHeadlineColor"
        :poll-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :break-uuid="storeBreak.breakUuid"
        @poll-input-emitted="handlePokeData"
      />

      <PollOutput
        v-show="shouldShowResults"
        :output-box-background="backgroundHandler()"
        ad-vote-state="Vote Received"
        :ad-tagline="storeBreak.breakTaglineCopy"
        :ad-tagline-color="storeBreak.breakTaglineColor"
        :poll-options="storeBreak.breakOptions"
        :focus-index="selectedPollOption"
        :break-uuid="storeBreak.breakUuid"
        :poll-percentages="calculatedPollPercentages"
        :bar-color="options.pollOutputColors.bar"
        :results-box-background-color="options.pollOutputColors.resultsBox"
      />

      <div class="pip-poll__instructions">
        <img class="pip-poll__use-remote" :src="pollUseRemoteSrc" />

        <img class="pip-poll__remote-img" :src="pollRemoteTopFadeSrc" />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped></style>
